import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import Arrow from '../../assets/imgs/chats/arrow.svg';
import { RecipientButton } from '../../components/RecipientButton/RecipientButton';
import { sendAMessage } from '../CreateMessage/createMessageSlice/actionCreators';
import { useTenantId } from '../../utils/customHooks/useTenantId';
import { useTranslation } from 'react-i18next';
import { saveDocumentToServer } from '../../apis/mediaAPI';
import ChatBox from '../../components/Chat/ChatBox';
import { SimpleText } from '../../components/Chat/ChatListItem.styles';
import { FilterOrSelectBottomSheet } from '../../components/FilterOrSelectBottomSheet/FilterOrSelectBottomSheet';
import Loader from '../../components/Loader/Loader';
import { useAppDispatch, useAppSelector } from '../../hooks';
import type { IamOkMessageMembersType } from '../../routes/ImOk';
import type { CreateMessageModel } from '../Chat/models';
import { selectGroupById } from '../GroupsList/groupsSlice';
import { fetchGroups } from '../GroupsList/groupsSlice/actionCreators';
import { selectUserId } from '../Login/LoginSlice';
import { getRecipientsText, useGetMembers } from '../StartIamOkMessage/helpers';
import { getIamOkInitiallSelected } from './createMessageIamOkSummary.helpers';
import {
  GroupContainer,
  SContentWrapper,
  SFlexDiv,
  SIcon,
  SItem,
  SLine,
  SPage,
} from './createMessageIamOkSummary.styles';
import { selectImOkDocument, selectImOkIsLoading } from './imOkSlice';
import { fetchImOkDocument } from './imOkSlice/actionCreators';
import type { SelectedAllType } from '../../components/FilterOrSelectBottomSheet/types';

export const CreateMessageIamOkSummary = () => {
  const imOkDocument = useAppSelector(selectImOkDocument);
  const isLoading = useAppSelector(selectImOkIsLoading);

  const { membersType, id } = useParams();
  const dispatch = useAppDispatch();

  const fetchImOk = async () => {
    dispatch(fetchImOkDocument(id || ''));
  };
  const navigate = useNavigate();
  const groupId = imOkDocument?.groups[0].id || 0;
  const tenantId = useTenantId();
  const userId = useAppSelector(selectUserId);

  const selectedGroup = useAppSelector(selectGroupById(groupId));

  const { members: membersForFilter, isGettingMembers } = useGetMembers({
    id: groupId,
    tenantId,
  });

  const [userIds, setUserIds]: [Set<number>, (set: Set<number>) => void] = useState(new Set());
  const [recipientsFilter, setRecipientsFilter] = useState<boolean>(false);
  const [tabBar, setTabBar] = useState<boolean>(true);

  const onFilter = (selected: Set<number> | SelectedAllType) => {
    switch (selected) {
      case 'SELECTED_ALL': {
        const selectedUserIds = new Set(membersForFilter.map(member => member.userID));
        setUserIds(selectedUserIds);
        setRecipientsFilter(false);
        break;
      }
      case 'UNSELECTED_ALL':
        break;
      default:
        setUserIds(selected);
        setRecipientsFilter(false);
        break;
    }
  };

  const selectedGroups = selectedGroup ? [selectedGroup] : [];

  useEffect(() => {
    dispatch(fetchGroups());
  }, []);

  useEffect(() => {
    if (!imOkDocument) {
      fetchImOk();
    } else {
      const selected = getIamOkInitiallSelected({
        meesageType: membersType as IamOkMessageMembersType,
        iamOk: imOkDocument,
        // withoutCurrentUserId:  userId
      });

      setUserIds(selected);
    }
  }, [imOkDocument]);

  const { t } = useTranslation();

  const getIDs = <T extends { id: number }>(list: T[]) => {
    return list.map(item => item.id);
  };

  const sendMessage = (
    text?: string,
    imageFileNames?: string[],
    documentFileNames?: string[],
    audioFileNames = [],
    locationId?: number
  ) => {
    const recipientsIDs: number[] = [...userIds];
    const groupsIDs: number[] = getIDs(selectedGroups);

    const messageModel: CreateMessageModel = {
      subject: null,
      senderId: userId,
      groupIds: groupsIDs.length === 0 ? undefined : groupsIDs,
      recipientIds: recipientsIDs.length === 0 ? null : recipientsIDs,
      documentFileNames: documentFileNames,
      audioFileNames: audioFileNames,
      photoFileNames: imageFileNames,
      text: text,
      locationId: locationId,
      // replyType: getReplyTypeId(replyType),
    };

    dispatch(sendAMessage(messageModel, navigate));
  };

  const onPhotosSend = async (imagesList: string[], messageText: string) => {
    if (imagesList.length > 0) {
      const text = messageText && messageText.length > 0 ? messageText : undefined;
      sendMessage(text, imagesList);
    }
  };

  const onDocumentsSend = async (documentsList: File[]) => {
    const documentsFilesNamesArray = [];
    let document: File;
    for await (document of documentsList) {
      const formData = new FormData();
      formData.append('document', document);
      const result = await saveDocumentToServer(formData);
      if (result) {
        documentsFilesNamesArray.push(result);
      }
    }
    if (documentsFilesNamesArray.length > 0) {
      sendMessage(undefined, [], documentsFilesNamesArray);
    }
  };

  const renderToInput = () => {
    return (
      <SimpleText fontSize="14px">
        {getRecipientsText(membersForFilter, userIds || new Set())}
      </SimpleText>
    );
  };

  if (!imOkDocument && isLoading) return <Loader />;

  return (
    <SPage>
      <SContentWrapper>
        <GroupContainer>
          {selectedGroups.length !== 0 && (
            <>
              {selectedGroups.map((group, key) => {
                return (
                  <RecipientButton
                    key={key}
                    name={group?.name}
                    onClick={() => navigate(`/muster/${id}`)}
                  />
                );
              })}
            </>
          )}
        </GroupContainer>
        {!isGettingMembers && (
          <FilterOrSelectBottomSheet
            isOpen={recipientsFilter}
            setIsOpen={setRecipientsFilter}
            onFilter={onFilter}
            data={membersForFilter}
            initialSelected={userIds}
            withPhoto
            atLeastOneReq
            selectShapeType="box"
            titleTx="messages_selectRecepients"
            selectAllTx={'imOk_allRecepients'}
            hideCurrentUserId={userId}
          />
        )}
        <SLine className="moreMargin" />

        <SItem onClick={() => setRecipientsFilter(!recipientsFilter)}>
          <SimpleText className="left" fontSize="12px">
            {t(`messages_to`)}
          </SimpleText>
          {renderToInput()}
          <SFlexDiv />
          <SIcon src={Arrow} alt="" />
        </SItem>
        <SLine />
      </SContentWrapper>
      <ChatBox
        tabBar={tabBar}
        setTabBar={setTabBar}
        onTextSend={sendMessage}
        onPhotosSend={onPhotosSend}
        onDocumentsSend={onDocumentsSend}
        onLocationSend={(locationId, text) => sendMessage(text, [], [], [], locationId)}
      />
    </SPage>
  );
};
